import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Empty, Modal, message } from "antd";
import { apiConfig } from "../../authConfig";
import { apiErrorFromResponse, userMessageForError } from "../../utils/apiError";
import { formatMetaDate, reportExportReadiness } from "../../utils/internalAuditView";
import { iaRequest } from "./iaApi";
import { ReportTextPanel } from "./ReportTextPanel";
import {
  EMPTY_SECTION_PROMPT,
  InternalAuditReportDocument,
  InternalAuditReportModalTitle,
} from "./InternalAuditReportDocument";
import { displayEmail } from '../../utils/displayEmail';

const FINDING_TYPES = [
  { key: "Conformity", bar: "bg-sky-400", label: "Conformity" },
  { key: "NC Major", bar: "bg-rose-400", label: "NC Major" },
  { key: "NC Minor", bar: "bg-orange-400", label: "NC Minor" },
  { key: "Observation", bar: "bg-amber-400", label: "Observation" },
];

function coverageBarColor(percent) {
  if (percent >= 80) return "bg-[color:var(--color-accent-fill)]";
  if (percent >= 60) return "bg-[color:var(--color-warn-fg)]";
  return "bg-orange-500";
}

function DetailRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[color:var(--color-border)] py-2.5 text-sm last:border-0">
      <span className="font-medium text-[color:var(--color-text-muted)]">{label}</span>
      <span className={`text-right font-semibold text-[color:var(--color-text-hero)] ${highlight ? "font-black" : ""}`}>
        {value}
      </span>
    </div>
  );
}

/**
 * Internal Audit Report — dashboard metrics + AI narrative + DOCX export.
 */
export function AuditReportSubTab({
  projectId,
  project,
  /** Generating the report stamps it "pending SPOC review": a Lead Advisor act (platform_admin is refused). */
  canGenerate = false,
  /** Formal sign-off is a Company SPOC act (API: 403 for every other role, including an impersonated SPOC). */
  canSpocSignOff = false,
  /** Writing / accepting report text: project Lead Advisor or Advisor (API: 403 for admin, SPOC, delegate). */
  canWriteText = false,
  getApiToken,
  onPackageUpdated,
  onSwitchTab,
}) {
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [signingOff, setSigningOff] = useState(false);
  // 409 blockers / 403 messages from the sign-off call, shown until the next attempt or refresh.
  const [signOffIssue, setSignOffIssue] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError("");
    try {
      const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit/report`);
      setSnapshot(data.snapshot || null);
      if (data.internalAuditPackage && onPackageUpdated) {
        onPackageUpdated(data.internalAuditPackage, { refreshSummary: true });
      }
    } catch (e) {
      setLoadError(userMessageForError(e, "Could not load audit report."));
    } finally {
      setLoading(false);
    }
  }, [projectId, getApiToken, onPackageUpdated]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const findingsMax = useMemo(() => {
    if (!snapshot?.findingsSummary?.counts) return 1;
    return Math.max(1, ...Object.values(snapshot.findingsSummary.counts));
  }, [snapshot]);

  const hasFindings = snapshot?.hasFindings;
  const hasReport = Boolean(snapshot?.auditReport?.generatedAt);
  const readiness = useMemo(() => reportExportReadiness(snapshot), [snapshot]);
  const signedOff = snapshot?.metadata?.signOffStatus === "approved";
  const unclassified = Number(snapshot?.unclassifiedQuestionCount) || 0;

  const handleGenerate = useCallback(async () => {
    if (!projectId) return;
    setGenerating(true);
    try {
      const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit/generate-report`, {
        method: "POST",
        body: {},
      });
      setSnapshot(data.snapshot || null);
      if (data.internalAuditPackage && onPackageUpdated) {
        onPackageUpdated(data.internalAuditPackage, { refreshSummary: true });
      }
      message.success("AI draft generated. Review it and accept the sections you want in the report.");
    } catch (e) {
      message.error(userMessageForError(e, "Report generation failed."));
    } finally {
      setGenerating(false);
    }
  }, [projectId, getApiToken, onPackageUpdated]);

  // Cl. 9.2 / 9.3, Workflow slide 13: the Company SPOC's recorded approval of the report and action plan.
  const handleSignOff = useCallback(async () => {
    if (!projectId) return;
    setSigningOff(true);
    setSignOffIssue(null);
    try {
      const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit/sign-off`, {
        method: "POST",
        body: {},
      });
      if (data?.internalAuditPackage && onPackageUpdated) {
        onPackageUpdated(data.internalAuditPackage, { refreshSummary: true });
      }
      message.success("Internal audit report signed off. Stage 10 (Approved document library) can now be published.");
      await fetchSnapshot();
    } catch (e) {
      const blockers = Array.isArray(e?.details?.blockers) ? e.details.blockers.filter((b) => typeof b === "string" && b) : [];
      setSignOffIssue({
        status: e?.status ?? 0,
        reason: e?.details?.reason || "",
        message: userMessageForError(e, "Sign-off failed."),
        blockers,
      });
      // A 409 "already signed off" means our view is stale: reload so the panel shows the truth.
      if (e?.status === 409 && blockers.length === 0) await fetchSnapshot();
    } finally {
      setSigningOff(false);
    }
  }, [projectId, getApiToken, onPackageUpdated, fetchSnapshot]);

  const confirmSignOff = useCallback(() => {
    Modal.confirm({
      title: "Sign off the internal audit report?",
      content:
        "This is a formal approval of the internal audit report and corrective action plan, recorded in the audit log under your name. Once signed off, the report text is locked and Stage 10 (Approved document library) can be published. This cannot be undone.",
      okText: "Sign off",
      cancelText: "Cancel",
      onOk: handleSignOff,
    });
  }, [handleSignOff]);

  const handleDownload = useCallback(async () => {
    if (!projectId || !hasFindings) {
      message.warning("Complete audit findings before downloading the report.");
      return;
    }
    if (!readiness.ready) {
      message.warning(`${readiness.missing.join(" and ")} must be written before the report can be exported.`);
      return;
    }
    setDownloading(true);
    try {
      const token = await getApiToken();
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${projectId}/internal-audit/report/docx`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        message.error(apiErrorFromResponse(res, json).message);
        return;
      }
      const blob = await res.blob();
      const disp = res.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : "Internal-Audit-Report.docx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      message.success("Report downloaded.");
    } catch (e) {
      message.error(userMessageForError(e, "Download failed."));
    } finally {
      setDownloading(false);
    }
  }, [projectId, getApiToken, hasFindings, readiness]);

  if (loading && !snapshot) {
    return (
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-16 text-center text-sm font-medium text-[color:var(--color-text-muted)]">
        Loading audit report…
      </div>
    );
  }

  if (loadError) {
    return (
      <div role="alert" className="rounded-3xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] p-8 text-center shadow-sm">
        <p className="text-sm font-medium text-[color:var(--color-danger-fg)]">{loadError}</p>
        <div className="mt-4">
          <Button onClick={() => void fetchSnapshot()}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!hasFindings) {
    return (
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-12 shadow-sm">
        <Empty
          description={
            <span className="text-sm font-medium text-[color:var(--color-text-muted)]">
              Generate audit findings before creating the internal audit report.
            </span>
          }
        />
        {onSwitchTab ? (
          <div className="mt-6 flex justify-center">
            <Button type="primary" onClick={() => onSwitchTab("audit_findings")}>
              Go to Audit Findings
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  const meta = snapshot?.metadata || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-[color:var(--color-text-hero)]">Internal Audit Report</h3>
          <p className="mt-0.5 text-sm font-medium text-[color:var(--color-text-muted)]">
            Summary metrics are computed from your audit data. Narrative sections are written by you or drafted by AI and reviewed.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            disabled={!hasFindings}
            onClick={() => setPreviewOpen(true)}
          >
            View report PDF
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={downloading}
            disabled={!hasFindings || !readiness.ready}
            onClick={handleDownload}
          >
            Download report
          </Button>
          {canGenerate ? (
            <Button
              icon={hasReport ? <ReloadOutlined /> : <FileTextOutlined />}
              loading={generating}
              onClick={() => {
                if (hasReport) {
                  Modal.confirm({
                    title: "Regenerate audit report?",
                    content: "This replaces the AI draft with a new one. Text you wrote and the opinion are kept; accepted sections stay accepted until you accept the new draft.",
                    okText: "Regenerate",
                    onOk: handleGenerate,
                  });
                } else {
                  handleGenerate();
                }
              }}
            >
              {hasReport ? "Regenerate AI draft" : "Generate AI draft"}
            </Button>
          ) : null}
        </div>
      </div>

      {hasFindings && !readiness.ready ? (
        <div
          data-testid="report-export-blocked"
          className="rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]"
        >
          <strong>{readiness.missing.join(" and ")}</strong> {readiness.missing.length > 1 ? "are" : "is"} empty.{" "}
          {EMPTY_SECTION_PROMPT}. The report cannot be exported until both are written.
        </div>
      ) : null}

      {unclassified > 0 ? (
        <div
          data-testid="unclassified-notice"
          className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)]"
        >
          {unclassified} question{unclassified === 1 ? "" : "s"} unclassified. They are excluded from the
          per-area coverage below until they are given a domain.
        </div>
      ) : null}

      <ReportTextPanel
        projectId={projectId}
        snapshot={snapshot}
        canWrite={canWriteText}
        getApiToken={getApiToken}
        onChanged={fetchSnapshot}
      />

      {signedOff ? (
        <div
          data-testid="signoff-complete"
          className="rounded-2xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-ok-fg)]"
        >
          Signed off by the Company SPOC
          {snapshot?.auditReport?.signedOffByEmail ? ` (${displayEmail(snapshot.auditReport.signedOffByEmail)})` : ""}
          {snapshot?.auditReport?.signedOffAt ? ` on ${formatMetaDate(snapshot.auditReport.signedOffAt)}` : ""}.
        </div>
      ) : canSpocSignOff ? (
        <div data-testid="signoff-panel" className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-[color:var(--color-text-muted)]">
              Review the report and the corrective action plan, then sign off. Stage 10 cannot be published until you do.
            </p>
            <Button type="primary" loading={signingOff} onClick={confirmSignOff}>
              Sign off internal audit report
            </Button>
          </div>
          {signOffIssue ? (
            <div data-testid="signoff-issue" role="alert" className="mt-3 rounded-xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] px-3 py-2 text-sm text-[color:var(--color-danger-fg)]">
              <p className="font-semibold">
                {signOffIssue.reason === "STAGE_PREREQUISITE_NOT_MET"
                  ? "A prerequisite stage is not complete."
                  : signOffIssue.status === 409 && signOffIssue.blockers.length
                    ? "The report cannot be signed off yet. Resolve the following:"
                    : "Sign-off was not recorded."}
              </p>
              {signOffIssue.blockers.length ? (
                <ul className="mt-1 list-disc pl-5">
                  {signOffIssue.blockers.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1">{signOffIssue.message}</p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
          <h4 className="mb-4 text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Findings Summary
          </h4>
          <div className="space-y-4">
            {FINDING_TYPES.map(({ key, bar, label }) => {
              const count = snapshot?.findingsSummary?.counts?.[key] ?? 0;
              const width = findingsMax > 0 ? Math.round((count / findingsMax) * 100) : 0;
              return (
                <div key={key}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="font-semibold text-[color:var(--color-text-muted-dark)]">{label}</span>
                    <span className="font-black text-[color:var(--color-text-hero)]">{count}</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-[color:var(--color-bg-alt)]">
                    <div
                      className={`h-full rounded-full transition-all ${bar}`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
          <h4 className="mb-4 text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Audit Coverage
          </h4>
          <div className="space-y-4">
            {(snapshot?.coverage || []).map((row) => {
              const measured = typeof row.percent === "number";
              return (
                <div key={row.label} data-coverage-row={row.label}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="font-semibold text-[color:var(--color-text-muted-dark)]">{row.label}</span>
                    <span
                      className="font-black text-[color:var(--color-text-hero)]"
                      title={measured ? undefined : "No answered question in this area yet"}
                    >
                      {measured ? `${row.percent}%` : "-"}
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-[color:var(--color-bg-alt)]">
                    {measured ? (
                      <div
                        className={`h-full rounded-full transition-all ${coverageBarColor(row.percent)}`}
                        style={{ width: `${Math.max(0, Math.min(100, row.percent))}%` }}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
          <h4 className="mb-2 text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Report Details
          </h4>
          <DetailRow label="Audit Type" value={meta.auditType || "-"} />
          <DetailRow label="Standard" value={meta.standard || "-"} />
          <DetailRow label="Lead Auditor" value={meta.leadAuditor || "-"} />
          <DetailRow label="Audit Start" value={formatMetaDate(meta.auditStart)} />
          <DetailRow label="Audit End" value={formatMetaDate(meta.auditEnd)} />
          <DetailRow label="Report Date" value={formatMetaDate(meta.reportDate)} />
          <DetailRow label="Total Clauses Audited" value={String(meta.totalClausesAudited ?? "-")} />
          <DetailRow label="Total Findings" value={String(meta.totalFindings ?? "-")} />
          <DetailRow label="Open CAPAs" value={String(meta.openCapas ?? "-")} />
          <DetailRow
            label="Sign-off Status"
            value={meta.signOffLabel || "-"}
            highlight={meta.signOffStatus === "pending_spoc"}
          />
        </div>
      </div>

      <Modal
        title={<InternalAuditReportModalTitle subtitle={snapshot?.view?.subtitle} />}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>
            Close
          </Button>,
          <Button
            key="dl"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            disabled={!hasFindings || !readiness.ready}
            onClick={handleDownload}
          >
            Download report
          </Button>,
        ]}
        width={920}
        styles={{ body: { paddingTop: 8 } }}
      >
        <div className="max-h-[75vh] overflow-y-auto px-1 pb-2">
          <InternalAuditReportDocument snapshot={snapshot} project={project} />
        </div>
      </Modal>
    </div>
  );
}
